import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { Sidebar } from 'primereact/sidebar';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';


const AdvancedSearch = ({ visible, setVisible }) => {

  // ---------------------- CONSTANTS ----------------------
  const navigate = useNavigate();
  const [loading, setIsLoading] = useState(false);
  const [message, setMessage] = useState();
  const [error, setError] = useState();

  const items = [
    { key: 'unAnnNatVariants', label: 'UniProt Natural Variant' },
    { key: 'clinicalSignificance', label: 'Clinvar Clinical significance' },
    { key: 'consequence', label: 'Clinvar Consequence' },
    { key: 'alphamissenseClass', label: 'Clinvar AlphaMissense class' },
    { key: 'disease', label: 'Clinvar Disease' },
    { key: 'unMoleculeProcessing', label: 'Molecule processing' },
    { key: 'unLocation', label: 'Location' },
    { key: 'unMotives', label: 'Motive' },
    { key: 'unDomains', label: 'UniProt Domain' },
    { key: 'interproDomain', label: 'InterPro Domain' },
    { key: 'unLigandsBindingSite', label: 'Binding Site' },
    { key: 'unAnnPropActiveSite', label: 'Active Site' },
    { key: 'phosPtms', label: 'PTMs PhosphoSitePlus®' },
    { key: 'unGlycosylations', label: 'Glycosylations' },
    { key: 'unLipidations', label: 'Lipidations' },
    { key: 'unModifiedRes', label: 'Modified Residue' },
    { key: 'unDisulfideBongBridges', label: 'Disulfide Bond Bridges' },
    { key: 'afConfidenceValue', label: 'pLDDT' },
    { key: 'unCrossLinks', label: 'CrossLink' },
    { key: 'inBindingRegion', label: 'IntAct Binding Region' },
    { key: 'family', label: 'Protein Family' }
  ].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

  const filterOptions = {
    unAnnNatVariants: ['Yes', 'No'],
    unMoleculeProcessing: ['CHAIN', 'SIGNAL', 'TRANSIT', 'PRO_'],
    unLocation: ['EXTRAMEM', 'TRANSMEM', 'INTRAMEM'],
    alphamissenseClass: ['benign', 'ambiguous', 'pathogenic'],
    clinicalSignificance: [
      'low_penetrance', 'affects', 'association', 'benign', 'likely_benign',
      'confers_sensitivity', 'conflicting_interpretations_of_pathogenicity',
      'drug_response', 'established_risk_allele', 'likely_pathogenic',
      'likely_risk_allele', 'pathogenic', 'protective', 'risk_factor',
      'uncertain_risk_allele', 'uncertain_significance'
    ],
    consequence: [
      'coding_sequence_variant', 'frameshift_variant', 'incomplete_terminal_codon_variant',
      'inframe_deletion', 'inframe_insertion', 'missense_variant', 'NMD_transcript_variant',
      'protein_altering_variant', 'splice_acceptor_variant', 'splice_donor_5th_base_variant',
      'splice_donor_region_variant', 'splice_donor_variant', 'splice_region_variant', 'start_lost',
      'start_retained_variant', 'stop_gained', 'stop_lost', 'stop_retained_variant',
      'synonymous_variant'
    ],
    phosPtms: [
      { label: 'Phosphorylation', value: 'p' }, { label: 'Glycosylation', value: 'gl' },
      { label: 'Glycation', value: 'ga' }, { label: 'Ubiquitination', value: 'ub' },
      { label: 'Acetylation', value: 'ac' }, { label: 'Methylation', value: 'm' },
      { label: 'Sumoylation', value: 'sm' }
    ]
  };

  const queryLevel = [
    { label: 'Protein level', value: 'protein' },
    { label: 'Residue level', value: 'residue' }
  ];

  // ---------------------- STATE ----------------------
  const [level, setLevel] = useState(undefined);
  const [filters, setFilters] = useState([{ id: Date.now(), key: null, value: null }]);

  // ---------------------- HELPERS ----------------------
  const handleFilterChange = (id, field, val) => {
    setFilters((prev) => prev.map((f) => (f.id === id ? { ...f, [field]: val } : f)));
  };

  const renderFilterInput = (filter) => {
    const { key, value } = filter;
    if (!key) return null;

    if (key === 'afConfidenceValue') {
      return (
        <div className="d-flex flex-wrap gap-2">
          <InputNumber
            value={value?.min || null}
            placeholder="Min pLDDT"
            onValueChange={(e) => handleFilterChange(filter.id, 'value', { ...value, min: e.value })}
            mode="decimal"
            showButtons
            min={0}
            max={100}
            className="fixed-width"
            required
          />
          <InputNumber
            value={value?.max || null}
            placeholder="Max pLDDT"
            onValueChange={(e) => handleFilterChange(filter.id, 'value', { ...value, max: e.value })}
            mode="decimal"
            showButtons
            min={0}
            max={100}
            className="fixed-width"
            required
          />
        </div>
      );
    }

    const placeholderMap = {
      unAnnPropActiveSite: 'eg.: Proton acceptor',
      disease: 'eg.: HGF-related_disorder',
      unLigandsBindingSite: 'eg.: Fe cation',
      unCrossLinks: 'eg.: interchain with G-Cter in SUMO2',
      unDisulfideBongBridges: 'eg.: SS_BOND_131',
      unGlycosylations: 'eg.: N-linked (GlcNAc...) asparagine',
      inBindingRegion: 'eg.: P17612 or PRKACA',
      interproDomain: 'eg.: Protein kinase-like domain',
      unLipidations: 'eg.: S-palmitoyl cysteine',
      unModifiedRes: 'eg.: Phosphotyrosine',
      unMotives: 'eg.: Nudix box',
      family: 'eg.: Belongs to the TMA7 family',
      unDomains: 'eg.: Protein kinase'
    };

    if (['unAnnPropActiveSite', 'disease', 'unLigandsBindingSite', 'unCrossLinks', 'unDisulfideBongBridges', 'unGlycosylations', 'inBindingRegion', 'interproDomain', 'unLipidations', 'unModifiedRes', 'unMotives', 'family', 'unDomains'].includes(key)) {
      return (
        <InputText
          value={value || ''}
          onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
          placeholder={placeholderMap[key] || 'Provide a value'}
          className="fixed-width"
          required
        />
      );
    }

    const opts = filterOptions[key];
    if (opts && Array.isArray(opts)) {
      const isObjectOptions = typeof opts[0] === 'object';
      return (
        <Dropdown
          value={value || null}
          onChange={(e) => handleFilterChange(filter.id, 'value', e.value)}
          options={opts}
          optionLabel={isObjectOptions ? 'label' : undefined}
          optionValue={isObjectOptions ? 'value' : undefined}
          placeholder="Select option"
          className="fixed-width"
          required
        />
      );
    }

    return null;
  };

  async function getProteinsByCriteria(criteria) {

    try {
    const query = {
            query: `
                 query ProteinsByCriteria($criteria: [CriterionInput!]!){
                    getProteinsByCriteria(criteria: $criteria, pagination: { offset: 0, limit: 10 }) {
                      protein {
                        gene {
                          geneSymbol
                        }
                         proteinAn
                         status
                         seqLength
                         summary
                      }
                    }
                  }
            `,
            variables: {
                criteria: criteria,
            },
        };

      const { data } = await axios.post('https://api.atlantis.bioinfolab.sns.it/graphql', query);

      if (data?.data?.getProteinsByCriteria.length > 0){
           navigate( '/browse/', { state: { 'data': data.data.getProteinsByCriteria, 'filters': criteria } });

      } else if (data?.data?.getProteinsByCriteria.length === 0) {
                setIsLoading(false);
                setMessage("No results found! Please change your search criteria and try again.");
                setTimeout(() => setMessage(null), 5000);

            return;
        } else if (data?.data?.errors){
               setIsLoading(false);
               setError("Oops... \n An error has occurred!");
               setTimeout(() => setError(null), 5000);
               return;
        }

    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async function getResiduesByCriteria(criteria) {

    try {
    const query = {
            query: `
                 query ResiduesByCriteria($criteria: [CriterionInput!]!){
                    getResiduesByCriteria(criteria: $criteria, pagination: { offset: 0, limit: 10 }) {
                      anPosition
                      protein {
                        gene {
                          geneSymbol
                        }
                         proteinAn
                         status
                         seqLength
                         summary
                      }
                    }
                  }
            `,
            variables: {
                criteria: criteria,
            },
        };

      const { data } = await axios.post('https://api.atlantis.bioinfolab.sns.it/graphql', query);
      if (data?.data?.getResiduesByCriteria.length > 0){
           navigate( '/browse/', { state: { 'data': data.data.getResiduesByCriteria, 'filters': criteria } });

      } else if (data?.data?.getResiduesByCriteria.length === 0) {
                setIsLoading(false);
                setMessage("No results found! Please change your search criteria and try again.");
                setTimeout(() => setMessage(null), 5000);

            return;
        } else if (data?.data?.errors){
               setIsLoading(false);
               setError("Oops... \n An error has occurred!");
               setTimeout(() => setError(null), 5000);
               return;
        }


    } catch (error) {
      console.error(error);
      setIsLoading(false);
      return [];
    }
  }

  const areFiltersValid = () => {
    return filters.every(f => {
      if (!f.key) return false;

      if (f.key === 'afConfidenceValue') {
        const val = f.value || {};
        const minValid = typeof val.min === 'number' && val.min >= 0;
        const maxValid = typeof val.max === 'number' && val.max >= 0;
        return minValid || maxValid;
      }
      return f.value !== null && f.value !== undefined && f.value !== '';
    });
  };

  const handleFormSubmit = (e) => {

    e.preventDefault();

    const criteria = filters
      .filter(f => f.key && f.value !== null && f.value !== undefined && f.value !== '')
      .map(({ key, value }) => {
        if (key === 'afConfidenceValue') {
          const min = value?.min ?? '';
          const max = value?.max ?? '';
          const stringValue = `${min}${min !== '' && max !== '' ? '-' : ''}${max}`;
          return { key, value: stringValue };
        }
        return {
          key,
          value: typeof value === 'object' ? JSON.stringify(value) : value,
        };
   });

    if (level === "protein"){
        setIsLoading(true);
        getProteinsByCriteria(criteria);
    } else if (level === "residue"){
        setIsLoading(true);
        getResiduesByCriteria(criteria);
    }

  };

  return (
    <div className="card d-flex flex-column vh-100 overflow-hidden">
      <Sidebar
        visible={visible}
        onHide={() => setVisible(false)}
        content={({ closeIconRef, hide }) => (
          <form onSubmit={handleFormSubmit} className="d-flex flex-column vh-100 bg-light">
            {/* ---------- HEADER ---------- */}
            <div className="d-flex align-items-center justify-content-between px-3 pt-3">
              <span className="d-inline-flex align-items-center gap-2">
                <img src="/assets/images/logo_ico.png" alt="Database" style={{ width: '30px' }} />
                <span className="fw-bold text-2xl color-adv-search">Advanced Search</span>
              </span>
              <Button
                ref={closeIconRef}
                onClick={hide}
                type="button"
                icon="pi pi-times"
                className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: '2rem', height: '2rem' }}
              />
            </div>

            {/* ---------- BODY ---------- */}
            <div className="overflow-auto flex-grow-1">
              <div className="mx-3 mt-4 mb-3">
                <p className="fs-1">Protein set explore</p>
                <p className="fs--1">
                  Browse our database to obtain a list of proteins/residues filtered by specific criteria
                </p>
                <Dropdown
                  value={level}
                  onChange={(e) => setLevel(e.value)}
                  options={queryLevel}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select query level"
                  className="fixed-width mb-2"
                  appendTo={document.body}
                  required
                />
                <span className="color-adv-search px-1">Add/remove filter</span>
                <Button
                  type="button"
                  icon="pi pi-plus"
                  className="p-button-success mx-1"
                  onClick={() => setFilters([...filters, { id: Date.now(), key: null, value: null }])}
                />
                <Button
                  type="button"
                  icon="pi pi-times"
                  className="p-button-danger"
                  onClick={() => setFilters(filters.slice(0, -1))}
                />
              </div>

              {filters.map((filter, index) => (
                <div key={filter.id} className="mx-3 mb-3">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {index !== 0 ? (
                      <div className="p-inputgroup flex-1">
                        <span className="p-inputgroup-addon">AND</span>
                        <Dropdown
                          value={filter.key}
                          onChange={(e) => handleFilterChange(filter.id, 'key', e.value)}
                          options={items}
                          optionLabel="label"
                          optionValue="key"
                          placeholder="Select Filter"
                          className="fixed-width-and"
                          appendTo={document.body}
                          required
                        />
                      </div>
                    ) : (
                      <Dropdown
                        value={filter.key}
                        onChange={(e) => handleFilterChange(filter.id, 'key', e.value)}
                        options={items}
                        optionLabel="label"
                        optionValue="key"
                        placeholder="Select Filter"
                        className="fixed-width"
                        appendTo={document.body}
                        required
                      />
                    )}
                    {renderFilterInput(filter)}
                  </div>
                </div>
              ))}
            </div>

            {/* ---------- FOOTER ---------- */}
            { message && (
            <div className="mx-3">
            <Message  severity="info" text={message} />
            </div>
            )}
            { error && (
            <div className="mx-3">
            <Message  severity="error" text={error} />
            </div>
            )}
            <div className="mt-auto">
              <hr className="mx-3 mb-3 border-0 border-top" />
              <div className="d-flex gap-2 mx-3 mb-3">
                <Button
                  type="button"
                  severity="danger"
                  onClick={hide}
                  icon="pi pi-times"
                  label="Cancel"
                  className="btn btn-info"
                />
                {loading ? (
                    <Button icon="pi pi-spin pi-spinner" label="Searching"  disabled={loading} type="submit" className="btn btn-info" />
                ) : (
                   <Button icon="pi pi-search" label="Search" type="submit"  disabled={level === undefined || !areFiltersValid() || loading} className="btn btn-info" />
                )}
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export default AdvancedSearch;
